import React, { useState } from 'react';
import { MdClose, MdImage, MdOpenInNew } from 'react-icons/md';
import { Loader } from 'lucide-react';
import styles from './AuditTrail.module.css';
import { formatDateTimeWithSeconds } from '../../utils/dateFormatter';

// Get API base URL for file paths
const getFileUrl = (filename) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://door-valve-elements-config.trycloudflare.com';
    return `${baseUrl}/files/${filename}`;
};

const AuditDetailsPanel = ({ slidingPanel, onClose }) => {
    const formatTimestamp = formatDateTimeWithSeconds;
    const [imageError, setImageError] = useState({});

    // Helper function to format value for display
    const formatValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value, null, 2);
            } catch {
                return String(value);
            }
        }
        return String(value);
    };

    // Check if value is a complex object (for special rendering)
    const isComplexObject = (value) => {
        return value !== null && typeof value === 'object' && !Array.isArray(value);
    };

    // Check if field is uploaded_docs
    const isUploadedDocs = (fieldName) => {
        return fieldName === 'uploaded_docs';
    };

    // Render uploaded docs as images
    const renderUploadedDocs = (docs, type) => {
        if (!docs || typeof docs !== 'object') {
            return <span className={styles.valueText}>-</span>;
        }

        const docEntries = Object.entries(docs);
        
        if (docEntries.length === 0) {
            return <span className={styles.valueText}>No documents</span>;
        }

        return (
            <div className={styles.docsGrid}>
                {docEntries.map(([key, value]) => {
                    const filename = value?.filename;
                    const originalName = value?.originalName || key;
                    const imageUrl = filename ? getFileUrl(filename) : null;
                    const hasError = imageError[`${type}-${key}`];

                    return (
                        <div key={key} className={styles.docItem}>
                            <span className={styles.docLabel}>{key.replace(/_/g, ' ')}</span>
                            {imageUrl && !hasError ? (
                                <div className={styles.docImageWrapper}>
                                    <img
                                        src={imageUrl}
                                        alt={originalName}
                                        className={styles.docImage}
                                        onError={() => setImageError(prev => ({ ...prev, [`${type}-${key}`]: true }))}
                                    />
                                    <a
                                        href={imageUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.docImageOverlay}
                                        title="Open in new tab"
                                    >
                                        <MdOpenInNew />
                                    </a>
                                </div>
                            ) : (
                                <div className={styles.docPlaceholder}>
                                    <MdImage />
                                    <span>{originalName}</span>
                                </div>
                            )}
                            <span className={styles.docFileName}>{originalName}</span>
                        </div>
                    );
                })}
            </div>
        );
    };

    if (!slidingPanel.isOpen) return null;

    return (
        <>
            <div className={styles.overlay} onClick={onClose}></div>
            <div className={styles.slidingPanel}>
                <div className={styles.panelHeader}>
                    <h2>Audit Details</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        <MdClose />
                    </button>
                </div>
                <div className={styles.panelContent}>
                    {slidingPanel.loading ? (
                        <div className={styles.panelLoading}>
                            <Loader className={styles.loadingIcon} />
                            <p>Loading details...</p>
                        </div>
                    ) : slidingPanel.data ? (
                        <>
                            <div className={styles.auditInfo}>
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>User Name:</span>
                                    <span className={styles.infoValue}>{slidingPanel.data.full_name || '-'}</span>
                                </div>
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Role:</span>
                                    <span className={styles.infoValue}>{slidingPanel.data.role_name || '-'}</span>
                                </div>
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Module:</span>
                                    <span className={styles.infoValue}>{slidingPanel.data.module_name || '-'}</span>
                                </div>
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Action Type:</span>
                                    <span className={styles.actionBadgePanel}>{slidingPanel.data.action_type || '-'}</span>
                                </div>
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Timestamp:</span>
                                    <span className={styles.infoValue}>{formatTimestamp(slidingPanel.data.timestamp)}</span>
                                </div>
                                <div className={`${styles.infoRow} ${styles.remarkRow}`}>
                                    <span className={styles.infoLabel}>Remark:</span>
                                    <span className={styles.infoValue}>{slidingPanel.data.remark || '-'}</span>
                                </div>
                            </div>

                            {slidingPanel.data.field_changes && slidingPanel.data.field_changes.length > 0 ? (
                                <div className={styles.changesContainer}>
                                    <h3 className={styles.changesTitle}>Changes Made</h3>
                                    {slidingPanel.data.field_changes.map((fieldChange, fieldIndex) => {
                                        // Check if this is the new format (old_value/new_value directly on fieldChange)
                                        // or old format (changes array with nested oldValue/newValue)
                                        const hasDirectValues = fieldChange.old_value !== undefined || fieldChange.new_value !== undefined;
                                        const hasChangesArray = fieldChange.changes && Array.isArray(fieldChange.changes);

                                        return (
                                            <div key={fieldChange.id || fieldIndex} className={styles.changeItem}>
                                                {/* Show field name */}
                                                <div className={styles.changePath}>
                                                    <span className={styles.pathLabel}>Field:</span>
                                                    <span className={styles.pathValue}>{fieldChange.field_name?.replace(/_/g, ' ')}</span>
                                                </div>

                                                {/* New format: old_value and new_value directly on fieldChange */}
                                                {hasDirectValues && (
                                                    <>
                                                        {isUploadedDocs(fieldChange.field_name) ? (
                                                            <div className={`${styles.valueComparison} ${styles.vertical}`}>
                                                                <div className={styles.oldValue}>
                                                                    <span className={styles.valueLabel}>Old Documents:</span>
                                                                    {renderUploadedDocs(fieldChange.old_value, 'old')}
                                                                </div>
                                                                <div className={styles.arrow}>↓</div>
                                                                <div className={styles.newValue}>
                                                                    <span className={styles.valueLabel}>New Documents:</span>
                                                                    {renderUploadedDocs(fieldChange.new_value, 'new')}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className={`${styles.valueComparison} ${isComplexObject(fieldChange.old_value) || isComplexObject(fieldChange.new_value) ? styles.vertical : ''}`}>
                                                                <div className={styles.oldValue}>
                                                                    <span className={styles.valueLabel}>Old Value:</span>
                                                                    <span className={`${styles.valueText} ${isComplexObject(fieldChange.old_value) ? styles.codeBlock : ''}`}>
                                                                        {formatValue(fieldChange.old_value)}
                                                                    </span>
                                                                </div>
                                                                <div className={styles.arrow}>{isComplexObject(fieldChange.old_value) || isComplexObject(fieldChange.new_value) ? '↓' : '→'}</div>
                                                                <div className={styles.newValue}>
                                                                    <span className={styles.valueLabel}>New Value:</span>
                                                                    <span className={`${styles.valueText} ${isComplexObject(fieldChange.new_value) ? styles.codeBlock : ''}`}>
                                                                        {formatValue(fieldChange.new_value)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                )}

                                                {/* Show reason for change if available */}
                                                {fieldChange.reason && (
                                                    <div className={styles.reasonContainer}>
                                                        <span className={styles.reasonLabel}>Reason for Change:</span>
                                                        <span className={styles.reasonValue}>{fieldChange.reason}</span>
                                                    </div>
                                                )}

                                                {/* Old format: changes array with nested values */}
                                                {hasChangesArray && fieldChange.changes.map((change, index) => (
                                                    <div key={index} className={styles.nestedChange}>
                                                        <div className={styles.changePath}>
                                                            <span className={styles.pathLabel}>Path:</span>
                                                            <span className={styles.pathValue}>{change.path?.replace(/_/g, ' ')}</span>
                                                        </div>
                                                        
                                                        {change.path === 'uploaded_docs' ? (
                                                            <div className={`${styles.valueComparison} ${styles.vertical}`}>
                                                                <div className={styles.oldValue}>
                                                                    <span className={styles.valueLabel}>Old Documents:</span>
                                                                    {renderUploadedDocs(change.oldValue, 'old')}
                                                                </div>
                                                                <div className={styles.arrow}>↓</div>
                                                                <div className={styles.newValue}>
                                                                    <span className={styles.valueLabel}>New Documents:</span>
                                                                    {renderUploadedDocs(change.newValue, 'new')}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className={`${styles.valueComparison} ${isComplexObject(change.oldValue) || isComplexObject(change.newValue) ? styles.vertical : ''}`}>
                                                                <div className={styles.oldValue}>
                                                                    <span className={styles.valueLabel}>Old Value:</span>
                                                                    <span className={`${styles.valueText} ${isComplexObject(change.oldValue) ? styles.codeBlock : ''}`}>
                                                                        {formatValue(change.oldValue)}
                                                                    </span>
                                                                </div>
                                                                <div className={styles.arrow}>{isComplexObject(change.oldValue) || isComplexObject(change.newValue) ? '↓' : '→'}</div>
                                                                <div className={styles.newValue}>
                                                                    <span className={styles.valueLabel}>New Value:</span>
                                                                    <span className={`${styles.valueText} ${isComplexObject(change.newValue) ? styles.codeBlock : ''}`}>
                                                                        {formatValue(change.newValue)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className={styles.noChanges}>
                                    <p>No changes found for this audit log</p>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className={styles.noChanges}>
                            <p>No data available</p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default AuditDetailsPanel;